import * as React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTheme } from '@mui/material/styles'
import type { CalendarColorKey } from '../../typings'
import { resolveColorByKey } from '../../eventColors'
import { COARSE_POINTER, TOUCH_TARGET_PX } from '../touch'

type Props = {
  label: string
  colorKey: CalendarColorKey
  on: boolean
  onToggle: () => void
  /** The source behind this chip failed to load (non-blocking, AC2). */
  unavailable?: boolean
  /**
   * SH-2102: renders a chevron and treats every click as "open the menu"
   * (via `onExpand`) instead of a plain on/off toggle. `onToggle` is unused
   * when this is set.
   */
  chevron?: boolean
  /** Required together with `chevron` — called with the click target, for menu anchoring. */
  onExpand?: (anchorEl: HTMLElement) => void
}

/**
 * A source / sub-type filter toggle. One geometry, two colour pairs — only the
 * background and text colour change between states.
 *
 * These are toggles: `aria-pressed` conveys on/off so a screen reader does not
 * announce five identical buttons whose state is colour-only (WCAG 1.4.1).
 * The OFF state uses `text.primary` on a light track to pass AA (fixes the
 * prototype's 3.90:1 off state, SPEC-FilterChip.md F-1).
 *
 * A failed source is flagged on its chip (grooming decision #8: error state on
 * the source filter, not a separate banner) with an icon and an accessible
 * description, so it is not conveyed by colour alone.
 */
export function FilterChip({
  label,
  colorKey,
  on,
  onToggle,
  unavailable,
  chevron,
  onExpand,
}: Props) {
  const theme = useTheme()
  // ON uses the same AA-safe treatment as event tiles (ink-on-tint for light
  // sources, solid + white for dark) so no chip ships white-on-light below AA.
  const onColors = resolveColorByKey(theme, colorKey)

  const background = on ? onColors.fill : theme.palette.grey[300]
  const color = on ? onColors.ink : theme.palette.text.primary

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (chevron) {
      onExpand?.(event.currentTarget)
      return
    }
    onToggle()
  }

  return (
    <Chip
      label={
        chevron ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{label}</span>
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          </Box>
        ) : (
          label
        )
      }
      size="small"
      onClick={handleClick}
      role="button"
      aria-pressed={chevron ? undefined : on}
      aria-haspopup={chevron ? 'menu' : undefined}
      aria-label={unavailable ? `${label}, couldn't load` : undefined}
      icon={
        unavailable ? (
          <ErrorOutline data-test-id="filter-chip-unavailable" />
        ) : undefined
      }
      // 44px touch target on touch devices (story AC), compact on desktop.
      sx={{
        '&.MuiChip-root': {
          background,
          color,
          borderRadius: '999px',
          height: { xs: `${TOUCH_TARGET_PX}px`, lg: '24px' },
          [COARSE_POINTER]: { height: `${TOUCH_TARGET_PX}px` },
          fontFamily: theme.typography.fontFamily,
          fontSize: '12px',
          fontWeight: 500,
        },
        '& .MuiChip-icon': {
          color: theme.palette.error.main,
        },
        '&.MuiChip-root:hover': {
          background: on ? onColors.fill : theme.palette.grey[300],
        },
      }}
    />
  )
}
